import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CopyOutlined, CheckOutlined, MenuOutlined, CloseOutlined,
  RocketOutlined, AppstoreOutlined, ThunderboltOutlined,
  ArrowLeftOutlined, SettingOutlined, MonitorOutlined,
  GlobalOutlined, BulbOutlined, LinkOutlined,
  ApiOutlined,
} from '@ant-design/icons';

const SECTIONS = [
  { id: 'intro', label: 'Introducción', icon: <ApiOutlined /> },
  { id: 'quick-start', label: 'Inicio Rápido', icon: <RocketOutlined /> },
  { id: 'what-is-n8n', label: '¿Qué es n8n?', icon: <BulbOutlined /> },
  { id: 'instances', label: 'Tipos de Instancia', icon: <AppstoreOutlined /> },
  { id: 'access', label: 'Acceso y Configuración', icon: <SettingOutlined /> },
  { id: 'interface', label: 'Interfaz de n8n', icon: <GlobalOutlined /> },
  { id: 'statuses', label: 'Estados del Servicio', icon: <ThunderboltOutlined /> },
  { id: 'events', label: 'Eventos', icon: <MonitorOutlined /> },
];

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative group rounded-lg overflow-hidden my-4">
      {language && <div className="bg-gray-800 dark:bg-gray-950 px-4 py-2 text-xs text-gray-400 font-mono border-b border-gray-700">{language}</div>}
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 px-4 py-4 text-sm font-mono overflow-x-auto leading-relaxed"><code>{code}</code></pre>
      <button onClick={handleCopy} className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors opacity-0 group-hover:opacity-100" title="Copiar">
        {copied ? <CheckOutlined className="text-green-400" /> : <CopyOutlined />}
      </button>
    </div>
  );
};

const DocsN8N: React.FC = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });
      const current = offsets.reduce((closest, section) =>
        section.top <= 120 && section.top > closest.top ? section : closest,
        { id: offsets[0].id, top: -Infinity });
      setActiveSection(current.id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setMobileMenuOpen(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><img src="/favicon-logo.png" alt="Kubiy" className="w-5 h-5" /></div>
              <span className="font-semibold text-gray-900 dark:text-white">Kubiy Cloud</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700"></div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">n8n</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"><ArrowLeftOutlined /> Volver al inicio</Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-gray-600 dark:text-gray-400">{mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          <aside className={`${mobileMenuOpen ? 'fixed inset-0 top-16 z-20 bg-white dark:bg-gray-900 p-6' : 'hidden'} lg:block lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto lg:py-8`}>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <button key={s.id} onClick={() => scrollTo(s.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeSection === s.id ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}`}>
                  <span className="text-base">{s.icon}</span>{s.label}
                </button>
              ))}
            </nav>
          </aside>

          <main className="flex-1 min-w-0 py-8 lg:py-12">
            <div className="max-w-3xl">

              {/* INTRO */}
              <section id="intro" className="mb-16 scroll-mt-24">
                <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-semibold">Documentación</span>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-3 mb-4">n8n</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  Kubiy n8n te permite desplegar una instancia de n8n — plataforma de automatización de workflows — en infraestructura
                  dedicada en AWS. Crea flujos de trabajo visuales conectando servicios y APIs sin escribir código.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <SettingOutlined className="text-xl text-purple-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Infra Dedicada</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">EC2 exclusiva con n8n preinstalado</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <GlobalOutlined className="text-xl text-purple-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Editor Visual HTTPS</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Interfaz web con SSL automático</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <BulbOutlined className="text-xl text-purple-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">400+ Integraciones</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Conectores nativos para servicios populares</p>
                  </div>
                </div>
              </section>

              {/* INICIO RÁPIDO */}
              <section id="quick-start" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Inicio Rápido</h2>
                <div className="space-y-6">
                  {[
                    { n: 1, title: 'Infraestructura', desc: 'Nombre, tipo de instancia, almacenamiento (20-100 GB) y región.' },
                    { n: 2, title: 'Configuración', desc: 'Puerto 5678 via HTTPS. Sin credenciales preconfiguradas: la cuenta admin se crea en el primer acceso.' },
                    { n: 3, title: 'Revisa y crea', desc: 'Confirma y paga. Kubiy provisiona EC2, n8n, networking y SSL en ~10 minutos.' },
                  ].map((s) => (
                    <div key={s.n} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full flex items-center justify-center text-sm font-bold">{s.n}</div>
                      <div className="flex-1"><h3 className="font-semibold text-gray-900 dark:text-white mb-2">{s.title}</h3><p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p></div>
                    </div>
                  ))}
                </div>
              </section>

              {/* QUÉ ES N8N */}
              <section id="what-is-n8n" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">¿Qué es n8n?</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  n8n es una plataforma de automatización de workflows open source. Permite conectar servicios, APIs y aplicaciones
                  mediante una interfaz visual de arrastrar y soltar, sin código (o con JavaScript/Python personalizado).
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Casos de Uso</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Marketing: conectar CRM, email, redes sociales</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Integración de datos: sincronizar DBs, hojas de cálculo, APIs</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Notificaciones: alertas por Slack, email, SMS</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> DevOps: automatizar deploys, monitoreo, backups</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> AI: integrar con OpenAI, webhooks, formularios</li>
                </ul>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Ventajas</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    ['Open Source', 'Código abierto, sin vendor lock-in'],
                    ['Self-Hosted', 'Tus datos en tu infraestructura'],
                    ['400+ Integraciones', 'Conectores nativos para servicios populares'],
                    ['Código Custom', 'JavaScript y Python en workflows'],
                    ['Webhooks', 'Datos externos en tiempo real'],
                    ['Cron', 'Ejecución programada por horario'],
                  ].map(([title, desc]) => (
                    <div key={title} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* INSTANCIAS */}
              <section id="instances" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Tipos de Instancia</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">vCPU</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">RAM</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Caso de uso</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['t3.small', '2', '2 GB', 'Dev / testing'],
                        ['t3.medium', '2', '4 GB', 'Producción pequeña (recomendado)'],
                        ['t3.large', '2', '8 GB', 'Producción mediana'],
                        ['m5.large', '2', '8 GB', 'Workflows de alto rendimiento'],
                      ].map(([t, cpu, ram, use]) => (
                        <tr key={t}><td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{t}</code></td><td className="py-3 px-4">{cpu}</td><td className="py-3 px-4">{ram}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{use}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Almacenamiento: 20, 30, 50 o 100 GB (EBS gp3). Regiones: US East, US West, EU Ireland.
                </p>
              </section>

              {/* ACCESO */}
              <section id="access" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Acceso y Configuración</h2>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Puerto</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Puerto</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Protocolo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody>
                      <tr><td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">5678</code></td><td className="py-3 px-4">HTTPS</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">n8n Web UI y API con SSL automático</td></tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Cuenta de Administrador</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  n8n no requiere credenciales preconfiguradas. La primera vez que accedes a la UI, se te pide crear una cuenta admin (email + contraseña).
                </p>
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-6">
                  <p className="text-sm text-amber-800 dark:text-amber-300"><strong>Importante:</strong> No olvides las credenciales del primer acceso. Si las pierdes, necesitarás acceder a la instancia directamente para resetearlas.</p>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Endpoint</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr><td className="py-3 px-4 font-medium">n8n URL</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">HTTPS para el editor visual (con SSL automático)</td></tr>
                      <tr><td className="py-3 px-4 font-medium">DNS Hostname</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">DNS persistente de Kubiy</td></tr>
                      <tr><td className="py-3 px-4 font-medium">Public IP</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">IP pública de la EC2 (se recomienda usar DNS)</td></tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* INTERFAZ */}
              <section id="interface" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Interfaz de n8n</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  n8n proporciona un editor visual completo accesible desde la n8n URL en el detalle del servicio.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Editor de Workflows</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Arrastrar y soltar nodos para crear flujos</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Nodos: HTTP, DBs, APIs, email, Slack, etc.</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Ejecución manual o programada (cron)</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Historial de ejecuciones con datos paso a paso</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Depuración: ver datos entre nodos</li>
                </ul>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Webhooks</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Cada workflow con un nodo Webhook genera una URL única para recibir datos externos en tiempo real y disparar el workflow automáticamente.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Credenciales de Servicios</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  n8n permite almacenar API keys, tokens OAuth y credenciales de servicios externos de forma cifrada.
                  Se pueden reutilizar en múltiples workflows.
                </p>
              </section>

              {/* ESTADOS */}
              <section id="statuses" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Estados del Servicio</h2>
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Estado</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Indicador</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['initializing', 'bg-yellow-400 animate-pulse', 'Provisiona EC2, instala n8n, networking y SSL (~10 min)'],
                        ['deploying', 'bg-blue-400 animate-pulse', 'CloudFormation configurando infraestructura'],
                        ['running', 'bg-green-500', 'n8n activo y accesible via HTTPS'],
                        ['stopped', 'bg-gray-400', 'Detenido. Datos conservados.'],
                        ['error', 'bg-red-500', 'Error en creación o ejecución'],
                        ['deleting', 'bg-red-400 animate-pulse', 'Eliminando todos los recursos'],
                      ].map(([st, color, desc]) => (
                        <tr key={st}><td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{st}</code></td><td className="py-3 px-4"><span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`}></span></td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acciones</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Acción</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Disponible en</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['Open n8n UI', 'running', 'Abre el editor visual de workflows'],
                        ['Refresh', 'Todos', 'Actualiza información del servicio'],
                        ['Delete', 'Todos (excepto deleting)', 'Elimina permanentemente'],
                      ].map(([a, avail, d]) => (
                        <tr key={a}><td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{a}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{avail}</td><td className="py-3 px-4 text-gray-600 dark:text-gray-400">{d}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300"><strong>Tip:</strong> Durante <code className="font-mono">initializing</code> y <code className="font-mono">deploying</code>, el dashboard hace polling cada 5 segundos. La creación toma ~10 minutos.</p>
                </div>
              </section>

              {/* EVENTOS */}
              <section id="events" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Eventos</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Registran acciones y cambios de estado. Pestaña <strong>Events</strong> en el detalle.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { type: 'info', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300', desc: 'Progreso de operaciones' },
                    { type: 'success', color: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300', desc: 'Operación completada' },
                    { type: 'warning', color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300', desc: 'Requiere atención' },
                    { type: 'error', color: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300', desc: 'Error en operación' },
                  ].map((e) => (
                    <div key={e.type} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.color}`}>{e.type}</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{e.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

            </div>
          </main>
        </div>
      </div>

      <footer className="border-t border-gray-200 dark:border-gray-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">&copy; {new Date().getFullYear()} Kubiy Cloud. Todos los derechos reservados.</span>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">kubiy.cloud</Link>
            <span>&middot;</span><a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Términos</a>
            <span>&middot;</span><a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocsN8N;